/**
 * GeoLevelTag.tsx — the compact "which geography is this?" badge (T-537).
 *
 * ONE component renders every level tag in the app, on purpose. Noreen has an open question about
 * whether the All-metrics card should carry a tag per metric (what this ships, her literal ask) or
 * be grouped by level instead; keeping the mapping in data (siteAnalysis.ts / scoreDefinitions.ts)
 * and the rendering here means that answer changes one file, not thirty-three.
 *
 * Muted by design: the tag is provenance, not a value. It must not compete with the number it sits
 * next to — hence muted-foreground and the smallest step of the type scale.
 */
import { GEO_LEVEL_LABELS, GEO_LEVEL_DESCRIPTIONS, type GeoLevel } from '@/lib/geoLevel'

export interface GeoLevelTagProps {
  level: GeoLevel
  /** Metric name, so the tooltip/screen-reader text says what the level belongs to. */
  metricLabel?: string
}

export function GeoLevelTag({ level, metricLabel }: GeoLevelTagProps) {
  const label = GEO_LEVEL_LABELS[level]
  const description = GEO_LEVEL_DESCRIPTIONS[level]
  return (
    <span
      // `title` carries the explanation on hover (notably the county one: "every tract in this
      // county shows the same value"), without adding a popover to a 33-row list.
      title={metricLabel ? `${metricLabel} — ${description}` : description}
      aria-label={`Geographic level: ${label}`}
      className="ml-1.5 shrink-0 rounded border border-border/60 px-1 py-px text-[10px]
                 font-medium leading-none text-muted-foreground align-middle"
    >
      {label}
    </span>
  )
}
